// src/pages/AdminDashboard.js

import React, { useState } from 'react';
import { Typography, Container, Button, Tabs, Tab, Box } from '@mui/material';
import UsersList from '../components/UsersList';
import UserForm from '../components/UserForm';
import GroupsList from '../components/GroupsList';
import GroupForm from '../components/GroupsForm';
import MealList from '../components/MealList';
import MealForm from '../components/MealForm';
import { useDispatch } from 'react-redux';
import { addUser, updateUser, deleteUser } from '../actions/userActions';
import { addGroup, updateGroup } from '../actions/groupActions';
import { addMeal, updateMeal, deleteMeal } from '../actions/mealActions';
import { useNavigate } from 'react-router-dom';

// A simple TabPanel helper component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State for Users management
  const [openUserForm, setOpenUserForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // State for Groups management
  const [openGroupForm, setOpenGroupForm] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);

  // State for Meals management
  const [openMealForm, setOpenMealForm] = useState(false);
  const [currentMeal, setCurrentMeal] = useState(null);

  // Tab state: 0 - Users, 1 - Groups, 2 - Meals
  const [currentTab, setCurrentTab] = useState(0);

  // --- USER HANDLERS ---
  const handleAddUser = () => {
    setCurrentUser(null);
    setOpenUserForm(true);
  };

  const handleEditUser = (user) => {
    setCurrentUser(user);
    setOpenUserForm(true);
  };

  const handleDeleteUser = (userId) => {
    dispatch(deleteUser(userId));
  };

  const handleUserSubmit = (userData) => {
    if (currentUser) {
      dispatch(updateUser(currentUser.user_id, userData));
    } else {
      dispatch(addUser(userData));
    }
    setOpenUserForm(false);
  };

  // --- GROUP HANDLERS ---
  const handleAddGroup = () => {
    setCurrentGroup(null);
    setOpenGroupForm(true);
  };

  const handleEditGroup = (group) => {
    setCurrentGroup(group);
    setOpenGroupForm(true);
  };

  const handleGroupSubmit = (groupData) => {
    if (currentGroup) {
      dispatch(updateGroup(currentGroup.group_id, groupData))
        .then(() => {
          setOpenGroupForm(false);
          setCurrentGroup(null);
        })
        .catch((error) => {
          console.error('Error updating group:', error);
        });
    } else {
      dispatch(addGroup(groupData))
        .then(() => {
          setOpenGroupForm(false);
        })
        .catch((error) => {
          console.error('Error adding group:', error);
        });
    }
  };

  const handleManageChildren = (group) => {
    navigate(`/group/${group.group_id}/children`);
  };

  const handleManageTeachers = (group) => {
    navigate(`/group/${group.group_id}/teachers`);
  };

  // --- MEAL HANDLERS ---
  const handleAddMeal = () => {
    setCurrentMeal(null);
    setOpenMealForm(true);
  };

  const handleEditMeal = (meal) => {
    setCurrentMeal(meal);
    setOpenMealForm(true);
  };

  const handleMealSubmit = (mealData) => {
    if (currentMeal) {
      dispatch(updateMeal(currentMeal.meal_id, mealData))
        .then(() => {
          setOpenMealForm(false);
          setCurrentMeal(null);
        })
        .catch((error) => {
          console.error('Error updating meal:', error);
        });
    } else {
      dispatch(addMeal(mealData))
        .then(() => {
          setOpenMealForm(false);
        })
        .catch((error) => {
          console.error('Error adding meal:', error);
        });
    }
  };

  const handleDeleteMeal = (mealId) => {
    dispatch(deleteMeal(mealId));
  };

  // --- TAB HANDLER ---
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Interfață de administrare
      </Typography>
      <Tabs value={currentTab} onChange={handleTabChange}>
        <Tab label="Utilizatori" />
        <Tab label="Grupuri" />
        <Tab label="Mese" />
      </Tabs>
      <TabPanel value={currentTab} index={0}>
        {/* Users Tab */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddUser}
          style={{ marginBottom: '16px' }}
        >
          Adaugă utilizator
        </Button>
        <UsersList onEdit={handleEditUser} onDelete={handleDeleteUser} />
        <UserForm
          open={openUserForm}
          onClose={() => setOpenUserForm(false)}
          onSubmit={handleUserSubmit}
          user={currentUser}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        {/* Groups Tab */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddGroup}
          style={{ marginBottom: '16px' }}
        >
          Adaugă Grupă
        </Button>
        <GroupsList
          onManageChildren={handleManageChildren}
          onManageTeachers={handleManageTeachers}
          onEdit={handleEditGroup}
        />
        <GroupForm
          open={openGroupForm}
          onClose={() => setOpenGroupForm(false)}
          onSubmit={handleGroupSubmit}
          group={currentGroup}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={2}>
        {/* Meals Tab */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddMeal}
          style={{ marginBottom: '16px' }}
        >
          Adaugă Meniu
        </Button>
        <MealList
          onEdit={handleEditMeal}
          onDelete={handleDeleteMeal}
        />
        <MealForm
          open={openMealForm}
          onClose={() => setOpenMealForm(false)}
          onSubmit={handleMealSubmit}
          meal={currentMeal}
        />
      </TabPanel>
    </Container>
  );
};

export default AdminDashboard;
